import React, { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Icon, Message } from 'semantic-ui-react'
import styled from 'styled-components'
import { useAuth } from 'context/user'
import { setCrmAccessDeniedHandler, setCrmUnauthorizedHandler } from 'utility/services/crm'
import { isCrmNotAllowed } from 'utility/crm/api-error'
import { CRM_BASE_PATH, canViewCrm } from 'utility/crm/permissions'
import { usePreference, useCrmLookups } from 'hooks/crm'
import { KpiTiles } from 'page-components/crm/KpiTiles'
import { CrmSearch } from 'page-components/crm/CrmSearch'
import { FollowupsBell } from 'page-components/crm/FollowupsBell'
import { ContactsView } from 'page-components/crm/ContactsView'
import { PipelineView } from 'page-components/crm/PipelineView'
import { AccountsView } from 'page-components/crm/AccountsView'
import { ImportWizard } from 'page-components/crm/ImportWizard'
import { CrmErrorBoundary } from 'page-components/crm/CrmErrorBoundary'
import { Row, TabBar, TabButton, darkControls } from 'page-components/crm/ui'

const VIEWS = [
    ['pipeline', 'Sales pipeline'],
    ['contacts', 'Contacts'],
    ['accounts', 'Accounts & health'],
]
const VIEW_KEYS = VIEWS.map(([key]) => key)

const Page = styled.div`
    color: ${({ theme }) => theme?.crm?.text || 'inherit'};
    background: ${({ theme }) => theme?.crm?.page || 'transparent'};
    min-height: calc(100vh - 110px);
    ${darkControls}
    padding: 0 16px 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    h1 {
        margin: 0;
        font-size: 1.5rem;
    }
`

/** CRM shell: header, KPI tiles, view tabs, global search, follow-ups bell. */
const CrmShell = () => {
    const { logout } = useAuth()
    const navigate = useNavigate()
    const { view } = useParams()
    const [params] = useSearchParams()
    const [lastView, setLastView] = usePreference('view', 'contacts')
    const [newContactOpen, setNewContactOpen] = useState(false)
    const [importOpen, setImportOpen] = useState(false)
    const [accessDenied, setAccessDenied] = useState(false)
    const lookups = useCrmLookups()
    const logoutRef = useRef(logout)
    logoutRef.current = logout

    // A 401 from any CRM call ends the session, like the rest of the app.
    // A 403 CRM_NOT_ALLOWED (access turned off, possibly after login) swaps the screen for a "no access" message.
    useEffect(() => {
        setCrmUnauthorizedHandler(() => logoutRef.current())
        setCrmAccessDeniedHandler(() => setAccessDenied(true))
        return () => {
            setCrmUnauthorizedHandler(null)
            setCrmAccessDeniedHandler(null)
        }
    }, [])

    const validView = VIEW_KEYS.includes(view)
    useEffect(() => {
        if (validView) setLastView(view)
    }, [view, validView])

    if (accessDenied || isCrmNotAllowed(lookups.error)) {
        return (
            <Page>
                <h1>CRM</h1>
                <Message warning icon role="alert">
                    <Icon name="lock" />
                    <Message.Content>
                        <Message.Header>You don&apos;t have access to CRM</Message.Header>
                        <p>Ask your administrator to turn it on for your account. If they just did, sign out and sign in again so it takes effect.</p>
                        <Button type="button" size="small" onClick={() => logoutRef.current()}>
                            Sign out
                        </Button>
                    </Message.Content>
                </Message>
            </Page>
        )
    }
    if (!validView) {
        return <Navigate to={`${CRM_BASE_PATH}/${VIEW_KEYS.includes(lastView) ? lastView : 'contacts'}`} replace />
    }

    const contactParam = Number(params.get('contact'))
    const openContactId = Number.isInteger(contactParam) && contactParam > 0 ? contactParam : null

    const dealParam = Number(params.get('deal'))
    const openDealId = Number.isInteger(dealParam) && dealParam > 0 ? dealParam : null
    const openDeal = id => navigate(`${CRM_BASE_PATH}/pipeline${id ? `?deal=${id}` : ''}`, { replace: !id })

    const openContact = id => {
        navigate(`${CRM_BASE_PATH}/contacts${id ? `?contact=${id}` : ''}`, { replace: !id })
    }
    const accountParam = Number(params.get('account'))
    const openAccountId = Number.isInteger(accountParam) && accountParam > 0 ? accountParam : null
    const openAccount = (id, replace = false) => navigate(`${CRM_BASE_PATH}/accounts${id ? `?account=${id}` : ''}`, { replace: replace || !id })
    const openDealFromElsewhere = id => navigate(`${CRM_BASE_PATH}/pipeline?deal=${id}`)

    const onSearchSelect = hit => {
        if (hit.Kind === 'contact') openContact(hit.Id)
        else if (hit.Kind === 'account') openAccount(hit.Id)
        else if (hit.Kind === 'deal') navigate(`${CRM_BASE_PATH}/pipeline?deal=${hit.Id}`)
        else if (hit.ContactId) openContact(hit.ContactId)
        else if (hit.AccountId) openAccount(hit.AccountId)
    }

    const newContact = () => {
        if (view !== 'contacts') navigate(`${CRM_BASE_PATH}/contacts`)
        setNewContactOpen(true)
    }

    return (
        <Page>
            <div>
                <h1>CRM</h1>
                <p style={{ margin: 0, opacity: 0.7 }}>Accounts, contacts, pipeline and follow-ups, linked to inspections and tickets.</p>
            </div>

            <Row $gap={10} $wrap>
                <CrmSearch onSelect={onSearchSelect} />
                <FollowupsBell onOpenContact={openContact} onOpenAccount={openAccount} />
                <Button type="button" basic onClick={() => setImportOpen(true)}>
                    <Icon name="upload" />
                    Import
                </Button>
                <Button primary type="button" onClick={newContact}>
                    <Icon name="plus" />
                    New contact
                </Button>
            </Row>

            {lookups.error ? (
                <Message negative role="alert">
                    <Message.Header>Could not load CRM settings</Message.Header>
                    {lookups.error.message}{' '}
                    <Button size="mini" onClick={lookups.retry}>
                        Try again
                    </Button>
                </Message>
            ) : null}

            <KpiTiles />

            <TabBar role="tablist" aria-label="CRM views">
                {VIEWS.map(([key, text]) => (
                    <TabButton
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={view === key}
                        $active={view === key}
                        onClick={() => navigate(`${CRM_BASE_PATH}/${key}`)}
                    >
                        {text}
                    </TabButton>
                ))}
            </TabBar>

            <CrmErrorBoundary resetKey={view}>
            {view === 'contacts' ? (
                <ContactsView
                    openContactId={openContactId}
                    onOpenContact={openContact}
                    newContactOpen={newContactOpen}
                    onCloseNewContact={() => setNewContactOpen(false)}
                />
            ) : null}
            {view === 'pipeline' ? <PipelineView openDealId={openDealId} onOpenDeal={openDeal} onOpenContact={openContact} /> : null}
            {view === 'accounts' ? (
                <AccountsView openAccountId={openAccountId} onOpenAccount={openAccount} onOpenContact={openContact} onOpenDeal={openDealFromElsewhere} />
            ) : null}
            </CrmErrorBoundary>
            <ImportWizard open={importOpen} onClose={() => setImportOpen(false)} />
        </Page>
    )
}

/**
 * Route entry. Users without the CRM flag are sent back to /app BEFORE any CRM data hook runs, so they never
 * make a CRM request (which the server would refuse with 403 CRM_NOT_ALLOWED anyway).
 */
export const CrmScreen = () => {
    const { user } = useAuth()
    if (!canViewCrm(user)) return <Navigate to="/app/task" replace />
    return <CrmShell />
}
